// Owner console: platform sign-in (Google or email OTP, then a mandatory second factor: TOTP or a
// recovery code) and the shell with the
// 13 IA §3 sections. Cross-tenant views show aggregates only; nothing vendor-specific renders here
// in Phase 4.
import { lazy, Suspense, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { normalizeDigits } from "@ady/format";
import { t } from "@ady/i18n";
import {
  Banner,
  Button,
  CrashPanel,
  ErrorBoundary,
  ErrorState,
  Field,
  focusFirstInvalid,
  Icon,
  Input,
  Logo,
  StatePanel,
  ToastProvider,
} from "@ady/ui";
import { api, ApiError, errorMessage, signInErrorMessage } from "./api";
import { MissionControl } from "./MissionControl";
import { AuthenticatorSetup, MySecurity, RecoveryCodesPanel } from "./account";
import { SECTIONS, type ConsoleSection } from "./sections";
import { StepUpHost } from "./stepUp";
import { MobileNav, Sidebar, TopBar } from "./shell";

// Every section is code-split: the console loads a section's code when it is opened.
const ImportMarketConsole = lazy(() =>
  import("./importMarket").then((m) => ({ default: m.ImportMarketConsole })),
);
const SmsServiceConsole = lazy(() =>
  import("./smsService").then((m) => ({ default: m.SmsServiceConsole })),
);
const WalletConsole = lazy(() => import("./wallet").then((m) => ({ default: m.WalletConsole })));
const AdsConsole = lazy(() => import("./ads").then((m) => ({ default: m.AdsConsole })));
const PaymentMethods = lazy(() =>
  import("./payments").then((m) => ({ default: m.PaymentMethods })),
);
const CoursesConsole = lazy(() => import("./courses").then((m) => ({ default: m.CoursesConsole })));
const OverviewConsole = lazy(() =>
  import("./overview").then((m) => ({ default: m.OverviewConsole })),
);
const PlansConsole = lazy(() => import("./plans").then((m) => ({ default: m.PlansConsole })));
const ShopDetailPage = lazy(() => import("./vendors").then((m) => ({ default: m.ShopDetailPage })));
const VendorsConsole = lazy(() => import("./vendors").then((m) => ({ default: m.VendorsConsole })));
const SupportConsole = lazy(() => import("./support").then((m) => ({ default: m.SupportConsole })));
const SubscriptionsConsole = lazy(() =>
  import("./subscriptions").then((m) => ({ default: m.SubscriptionsConsole })),
);
const PromosConsole = lazy(() => import("./promos").then((m) => ({ default: m.PromosConsole })));
const AnalyticsConsole = lazy(() =>
  import("./analytics").then((m) => ({ default: m.AnalyticsConsole })),
);
const ConfigConsole = lazy(() => import("./legal").then((m) => ({ default: m.ConfigConsole })));
const ReportsConsole = lazy(() => import("./reports").then((m) => ({ default: m.ReportsConsole })));
const SecurityConsole = lazy(() =>
  import("./security").then((m) => ({ default: m.SecurityConsole })),
);
const CapacityConsole = lazy(() => import("./usage").then((m) => ({ default: m.CapacityConsole })));
const JobsConsole = lazy(() => import("./usage").then((m) => ({ default: m.JobsConsole })));
const HealthConsole = lazy(() => import("./status").then((m) => ({ default: m.HealthConsole })));
const StorefrontsConsole = lazy(() =>
  import("./storefronts").then((m) => ({ default: m.StorefrontsConsole })),
);
const ThemePreviewTab = lazy(() =>
  import("./themes").then((m) => ({ default: m.ThemePreviewTab })),
);
const ThemesConsole = lazy(() => import("./themes").then((m) => ({ default: m.ThemesConsole })));
const IdeasConsole = lazy(() => import("./requests").then((m) => ({ default: m.IdeasConsole })));
const ReleasesConsole = lazy(() =>
  import("./releases").then((m) => ({ default: m.ReleasesConsole })),
);

export const TID = {
  signIn: "console-sign-in",
  google: "console-google",
  signInError: "console-sign-in-error",
  email: "console-email",
  sendCode: "console-send-code",
  code: "console-code",
  verify: "console-verify",
  mfa: "console-mfa",
  totp: "console-totp",
  totpVerify: "console-totp-verify",
  recovery: "console-recovery",
  switchFactor: "console-switch-factor",
  noFactor: "console-no-factor",
  shell: "console-shell",
  nav: "console-nav",
  navItem: (id: string) => `console-nav-${id}`,
  section: (id: string) => `console-section-${id}`,
  signOut: "console-sign-out",
  mockBanner: "mock-banner",
} as const;

const isMocked = () => Boolean((globalThis as { __ADY_MOCKS__?: boolean }).__ADY_MOCKS__);
const go = (router: ReturnType<typeof useRouter>, href: string) => router.history.push(href);

function MockBanner() {
  return isMocked() ? (
    <p
      data-testid={TID.mockBanner}
      role="note"
      className="bg-warning-bg px-4 py-1.5 text-center text-xs font-medium text-warning-fg"
    >
      {t("shell.mockBanner")}
    </p>
  ) : null;
}

/** The OWNER CONSOLE badge: a dark pill with a shield, under the logo. */
function ConsoleBadge() {
  return (
    <span className="inline-flex h-6.5 items-center gap-1.5 rounded-full bg-inverse ps-2 pe-2.5 text-2xs font-bold tracking-widest text-fg-inverse uppercase">
      <Icon name="shieldCheck" className="text-brand-green" />
      {t("console.mission.badge")}
    </span>
  );
}

/**
 * Console sign-in screens (owner design approved 2026-10-26): one wide card on a light page with a
 * faint control-room grid, root's mission control on the left (on top on a phone), the screen on the
 * right with the OWNER CONSOLE badge and three lines on how staff access is protected.
 */
function AuthFrame({
  title,
  lead,
  children,
  testId,
}: {
  title: string;
  lead: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <MockBanner />
      <main
        id="main"
        className="ady-auth-wash ady-console-grid relative flex flex-1 items-center justify-center overflow-hidden px-4 py-6 lg:px-8"
        data-testid={testId}
      >
        <div className="relative flex w-full max-w-280 flex-col overflow-hidden rounded-panel border border-card bg-surface shadow-3 lg:h-160 lg:flex-row">
          <div className="flex items-center justify-between gap-3 px-5 pt-5 lg:hidden">
            <Logo variant="lockup" height={24} className="max-w-full" />
            <ConsoleBadge />
          </div>
          <section
            aria-label={t("console.mission.label")}
            className="ady-bg-brand-tint relative h-90 flex-none lg:h-auto lg:w-[57%] lg:border-e lg:border-subtle"
          >
            <MissionControl />
          </section>
          <div className="flex flex-1 flex-col justify-center gap-5 px-5 py-6 sm:px-10 lg:px-12">
            <div className="hidden flex-col items-start gap-3 lg:flex">
              <Logo variant="lockup" height={32} className="max-w-full" />
              <ConsoleBadge />
            </div>
            <div className="flex flex-col gap-1.5">
              <h1 className="text-title-md font-bold">{title}</h1>
              <p className="text-sm text-fg-secondary">{lead}</p>
            </div>
            {children}
            <ul className="flex flex-col gap-2 border-t border-subtle pt-4 text-sm text-fg-secondary">
              {(
                [
                  ["lock", "twoStep"],
                  ["file", "recorded"],
                  ["users", "totals"],
                ] as const
              ).map(([icon, key]) => (
                <li key={key} className="flex items-center gap-2.5">
                  <Icon name={icon} className="flex-none text-link" />
                  {t(`console.mission.trust.${key}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
}

const sixDigits = (v: string) => normalizeDigits(v).replace(/\D/g, "").slice(0, 6);

/** A failed or unavailable Google round trip comes back as ?auth_error=… (the reason is only logged). */
function googleErrorFromUrl(): string | undefined {
  const code = new URLSearchParams(location.search).get("auth_error");
  if (code === "google") return t("console.googleFailed");
  if (code === "google_unavailable") return t("console.googleUnavailable");
  return undefined;
}

function SignIn() {
  const router = useRouter();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string>();
  const [pageError, setPageError] = useState<string | undefined>(googleErrorFromUrl);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  const google = () => {
    if (isMocked()) return setPageError(t("console.googleNeedsServer"));
    setLeaving(true);
    location.assign(api.googleStartUrl);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    if (step === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError(t("auth.emailInvalid"));
      requestAnimationFrame(() => focusFirstInvalid(form.current));
      return;
    }
    if (step === "code" && !/^\d{6}$/.test(code)) {
      setError(t("auth.codeInvalid"));
      requestAnimationFrame(() => focusFirstInvalid(form.current));
      return;
    }
    setBusy(true);
    try {
      if (step === "email") {
        const res = await api.otpStart(email.trim());
        setChallenge(res.challenge_id);
        setStep("code");
      } else {
        const res = await api.otpVerify(challenge, code);
        go(router, res.mfa_enrolled ? "/sign-in/mfa" : "/sign-in/mfa?enrolled=0");
      }
    } catch (err) {
      setError(signInErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthFrame
      title={t("console.signInTitle")}
      lead={step === "email" ? t("console.signInLead") : t("auth.verifyLead", { email })}
      testId={TID.signIn}
    >
      {pageError ? <Banner kind="error" title={pageError} data-testid={TID.signInError} /> : null}
      {step === "email" ? (
        <>
          <Button fullWidth onClick={google} loading={leaving} data-testid={TID.google}>
            {t("console.google")}
          </Button>
          <div className="flex items-center gap-3 text-sm text-fg-tertiary" aria-hidden="true">
            <span className="h-px flex-1 bg-line" />
            {t("auth.or")}
            <span className="h-px flex-1 bg-line" />
          </div>
        </>
      ) : null}
      <form ref={form} onSubmit={submit} noValidate className="flex flex-col gap-4">
        {step === "email" ? (
          <Field label={t("auth.emailLabel")} error={error} required>
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid={TID.email}
            />
          </Field>
        ) : (
          <Field label={t("auth.codeLabel")} error={error} required>
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(sixDigits(e.target.value))}
              ledger
              data-testid={TID.code}
              autoFocus
            />
          </Field>
        )}
        <Button
          type="submit"
          variant={step === "email" ? "secondary" : "primary"}
          fullWidth
          loading={busy}
          data-testid={step === "email" ? TID.sendCode : TID.verify}
        >
          {step === "email" ? t("auth.sendCode") : t("auth.verify")}
        </Button>
      </form>
    </AuthFrame>
  );
}

function Mfa() {
  const router = useRouter();
  const qc = useQueryClient();
  const enrolled = new URLSearchParams(location.search).get("enrolled") !== "0";
  const [method, setMethod] = useState<"totp" | "recovery">("totp");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  if (!enrolled) return <EnrollAuthenticator />;
  const finish = async () => {
    await qc.invalidateQueries();
    go(router, safeNext(new URLSearchParams(location.search).get("next")));
  };
  const recovery = method === "recovery";
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const value = recovery ? code.trim().toUpperCase() : code;
    const valid = recovery ? /^[A-Z0-9-]{10,16}$/.test(value) : /^\d{6}$/.test(value);
    if (!valid) {
      setError(recovery ? t("stepUp.recoveryInvalid") : t("auth.codeInvalid"));
      requestAnimationFrame(() => focusFirstInvalid(form.current));
      return;
    }
    setBusy(true);
    try {
      await (recovery ? api.recoveryCode(value) : api.totp(value));
      await finish();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const switchMethod = () => {
    setMethod(recovery ? "totp" : "recovery");
    setCode("");
    setError(undefined);
  };
  return (
    <AuthFrame title={t("auth.mfa.title")} lead={t("console.mfaLead")} testId={TID.mfa}>
      <form ref={form} onSubmit={submit} noValidate className="flex flex-col gap-4">
        {recovery ? (
          <Field label={t("stepUp.recoveryLabel")} error={error} required>
            <Input
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={16}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              ledger
              data-testid={TID.recovery}
              autoFocus
            />
          </Field>
        ) : (
          <Field label={t("auth.mfa.totpLabel")} error={error} required>
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(sixDigits(e.target.value))}
              ledger
              data-testid={TID.totp}
              autoFocus
            />
          </Field>
        )}
        <Button type="submit" fullWidth loading={busy} data-testid={TID.totpVerify}>
          {t("auth.mfa.verify")}
        </Button>
      </form>
      <Button variant="secondary" fullWidth onClick={switchMethod} data-testid={TID.switchFactor}>
        {recovery ? t("console.useTotp") : t("console.useRecovery")}
      </Button>
    </AuthFrame>
  );
}

/**
 * D-081: the first sign-in of a staff account without an authenticator sets one up right here, shows
 * the one-time recovery codes, then opens the workspace. The server refuses this once the account has
 * any second factor, so it can never add an app behind the owner's back.
 */
function EnrollAuthenticator() {
  const router = useRouter();
  const qc = useQueryClient();
  const enrolment = useQuery({
    queryKey: ["console", "enroll"],
    queryFn: api.mfaEnroll,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
  });
  const [codes, setCodes] = useState<string[] | null>(null);
  const open = async () => {
    await qc.invalidateQueries({ queryKey: ["me"] });
    go(router, "/");
  };
  const already =
    enrolment.error instanceof ApiError &&
    enrolment.error.code === "validation.mfa_already_enrolled";
  return (
    <AuthFrame
      title={t("console.enroll.title")}
      lead={t("console.enroll.lead")}
      testId={TID.noFactor}
    >
      {codes ? (
        <RecoveryCodesPanel
          codes={codes}
          doneLabel={t("console.enroll.openWorkspace")}
          onDone={() => void open()}
        />
      ) : enrolment.isPending ? (
        <p className="text-sm text-fg-secondary" aria-busy="true">
          {t("console.enroll.preparing")}
        </p>
      ) : enrolment.isError ? (
        <div className="flex flex-col gap-3">
          <Banner kind="error" title={errorMessage(enrolment.error)} />
          <Button
            variant="secondary"
            fullWidth
            onClick={() => go(router, already ? "/sign-in/mfa" : "/sign-in")}
          >
            {already ? t("console.useTotp") : t("common.back")}
          </Button>
        </div>
      ) : (
        <AuthenticatorSetup
          enrolment={enrolment.data}
          confirmLabel={t("console.enroll.confirm")}
          confirm={async (code) => (await api.mfaEnrollConfirm(code)).recovery_codes}
          onConfirmed={(recovery) => (recovery?.length ? setCodes(recovery) : void open())}
        />
      )}
    </AuthFrame>
  );
}

function SectionPage({ section }: { section: ConsoleSection }) {
  return (
    <div
      className="flex flex-col gap-4"
      data-testid={TID.section(section.id)}
      data-canonical={section.canonical}
    >
      {/* As in the vendor admin (PageHeader): the sidebar and section bar say where you are; the
          title stays for screen readers. */}
      <h1 className="ady-sr-only">{t(section.label)}</h1>
      {section.pendingActivation ? (
        <Banner kind="pending-activation" title={t("states.pendingActivationTitle")}>
          {t(section.pendingActivation)}
        </Banner>
      ) : null}
      <Page>
        {section.id === "overview" ? (
          <OverviewConsole />
        ) : section.id === "vendors" ? (
          <VendorsConsole />
        ) : section.id === "plans" ? (
          <PlansConsole />
        ) : section.id === "import" ? (
          <ImportMarketConsole />
        ) : section.id === "sms" ? (
          <SmsServiceConsole />
        ) : section.id === "ads" ? (
          <AdsConsole />
        ) : section.id === "courses" ? (
          <CoursesConsole />
        ) : section.id === "billing" ? (
          <WalletConsole />
        ) : section.id === "payments" ? (
          <PaymentMethods />
        ) : section.id === "subscriptions" ? (
          <SubscriptionsConsole />
        ) : section.id === "promos" ? (
          <PromosConsole />
        ) : section.id === "config" ? (
          <ConfigConsole />
        ) : section.id === "security" ? (
          <SecurityConsole />
        ) : section.id === "reports" ? (
          <ReportsConsole />
        ) : section.id === "capacity" ? (
          <CapacityConsole />
        ) : section.id === "jobs" ? (
          <JobsConsole />
        ) : section.id === "health" ? (
          <HealthConsole />
        ) : section.id === "storefronts" ? (
          <StorefrontsConsole />
        ) : section.id === "themes" ? (
          <ThemesConsole />
        ) : section.id === "analytics" ? (
          <AnalyticsConsole />
        ) : section.id === "support" ? (
          <SupportConsole />
        ) : section.id === "requests" ? (
          <IdeasConsole />
        ) : section.id === "releases" ? (
          <ReleasesConsole />
        ) : (
          <StatePanel
            kind="not-set-up"
            size="card"
            title={t("states.notSetUpTitle")}
            body={t(section.body)}
          />
        )}
      </Page>
    </div>
  );
}

/** A code-split section: a placeholder while its code loads, and a panel (not a blank page) if it
 * crashes; moving to another page clears the error. */
function Page({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <ErrorBoundary resetKey={pathname}>
      <Suspense
        fallback={<div className="h-72 animate-pulse rounded-card bg-sunken" aria-busy="true" />}
      >
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

/** D-066: the console shell, built like the vendor admin's (shell.tsx). */
function ConsoleShell() {
  const router = useRouter();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [navOpen, setNavOpen] = useState(false);
  const unauth = me.error instanceof ApiError && me.error.status === 401;
  useEffect(() => {
    if (unauth) go(router, signInPath(me.error));
  }, [unauth, router, me.error]);
  useEffect(() => setNavOpen(false), [pathname]);
  if (me.isPending || unauth) return <div className="min-h-dvh bg-canvas" aria-busy="true" />;
  if (me.isError) return <ErrorState size="page" onRetry={() => void me.refetch()} />;
  return (
    <div className="min-h-dvh bg-canvas" data-testid={TID.shell}>
      <a href="#main" className="ady-skip-link">
        {t("common.skipToContent")}
      </a>
      <MockBanner />
      <StepUpHost />
      <div className="flex">
        <Sidebar me={me.data} />
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar me={me.data} onMenu={() => setNavOpen(true)} />
          <MobileNav open={navOpen} onClose={() => setNavOpen(false)} />
          <main
            id="main"
            tabIndex={-1}
            data-testid={TID.nav}
            className="mx-auto w-full max-w-(--container-content) flex-1 px-4 pt-6 pb-12 outline-none md:px-6 lg:px-8"
          >
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: () => <StatePanel kind="error" size="page" title={t("errors.notFound")} />,
});
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  component: SignIn,
});
const mfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in/mfa",
  component: Mfa,
});
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "shell",
  component: ConsoleShell,
});
const sectionRoutes = SECTIONS.map((section) =>
  createRoute({
    getParentRoute: () => shellRoute,
    path: section.path,
    component: () => <SectionPage section={section} />,
  }),
);
// D-065: one shop's page in the Vendors section.
const shopRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/vendors/$orgId",
  component: () => (
    <div
      className="flex flex-col gap-4"
      data-testid={TID.section("vendors")}
      data-canonical="ORG-001, SYSTEM-001"
    >
      <Page>
        <ShopDetailPage />
      </Page>
    </div>
  ),
});
// D-091: Themes → Preview opens here in a new tab: signed in, but without the console frame.
function BareShell() {
  const router = useRouter();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false });
  const unauth = me.error instanceof ApiError && me.error.status === 401;
  useEffect(() => {
    if (unauth) go(router, signInPath(me.error));
  }, [unauth, router, me.error]);
  if (me.isPending || unauth) return <div className="min-h-dvh bg-canvas" aria-busy="true" />;
  if (me.isError) return <ErrorState size="page" onRetry={() => void me.refetch()} />;
  return (
    <>
      <StepUpHost />
      <Outlet />
    </>
  );
}
const bareRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "bare",
  component: BareShell,
});
const themePreviewRoute = createRoute({
  getParentRoute: () => bareRoute,
  path: "/themes/preview",
  component: () => (
    <Page>
      <ThemePreviewTab />
    </Page>
  ),
});
// D-081: account menu → My security (the staff member's own authenticator apps).
const mySecurityRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/account/security",
  component: MySecurity,
});
const routeTree = rootRoute.addChildren([
  signInRoute,
  mfaRoute,
  shellRoute.addChildren([...sectionRoutes, shopRoute, mySecurityRoute]),
  bareRoute.addChildren([themePreviewRoute]),
]);
export const router = createRouter({
  routeTree,
  // A crash outside a section (or a code file gone after a deploy) still shows a panel.
  defaultErrorComponent: ({ error, reset }) => (
    <CrashPanel size="page" error={error} onRetry={reset} />
  ),
});

/** Only a same-origin path is followed after the second factor. */
const safeNext = (next: string | null) =>
  next && /^\/[A-Za-z0-9]/.test(next) && !next.includes("\\") && !next.startsWith("/sign-in")
    ? next
    : "/";
const here = () => encodeURIComponent(location.pathname + location.search);
/** A session waiting for (or asked again for) the authenticator code goes to that step, not to Google. */
const signInPath = (err: unknown) =>
  err instanceof ApiError &&
  (err.code === "auth.mfa_required" || err.code === "auth.stepup_required")
    ? `/sign-in/mfa?next=${here()}`
    : "/sign-in";
// SECURITY §2.1 step-up: a sensitive action asks for the code again after 10 minutes. Inside the
// workspace the code popup (stepUp.tsx) handles it in place and the call is retried; this sign-in
// redirect is only the fallback for calls made outside it (raw uploads, no popup mounted).
const onAuthError = (err: unknown) => {
  if (err instanceof ApiError && err.code === "auth.stepup_required")
    router.history.push(signInPath(err));
};
const queryClient = new QueryClient({
  // As in the vendor admin: data stays fresh for 30 s, so moving between sections does not refetch
  // everything (boards that must stay live set their own refetchInterval).
  defaultOptions: { queries: { refetchOnWindowFocus: false, staleTime: 30_000 } },
  queryCache: new QueryCache({ onError: onAuthError }),
  mutationCache: new MutationCache({ onError: onAuthError }),
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </QueryClientProvider>
  );
}
